import { useContext, createContext, useState, useEffect, useCallback } from 'react'

import {
  modalUnbanMemberCommunityEventDispatch,
  modalUnbanMemberCommunityEventListener
} from '@space-events/toggleUnbanMemberCommunityModal'

import type { CommunityProps, UserBlockedFollowers } from '@space-shell/common/services/communities'

import type {
  UnbanMemberCommunityModalContextProps,
  UnbanMemberCommunityModalProviderProps
} from './types'

import UnbanMemberCommunityModal from '../..'
import ConfirmationUnBanModal from '../../components/confirmationUnbanModal'

export type UnbanMemberCommunityModalEventsPayload = {
  action: 'open' | 'close' | 'openConfirmation' | 'closeConfirmation'
  community?: CommunityProps
  user?: UserBlockedFollowers
  fromConfigurationSidebar?: boolean
}

const UnbanMemberCommunityModalContext = createContext<UnbanMemberCommunityModalContextProps>(
  {} as UnbanMemberCommunityModalContextProps
)

export const UnbanMemberCommunityModalProvider = ({
  children
}: UnbanMemberCommunityModalProviderProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = useState<boolean>(false)
  const [fromConfigurationSidebar, setFromConfigurationSidebar] = useState<boolean>(false)
  const [community, setCommunity] = useState<CommunityProps>({} as CommunityProps)
  const [inputSearch, setInputSearch] = useState<string>('')
  const [user, setUser] = useState<UserBlockedFollowers>({} as UserBlockedFollowers)

  const reset = () => {
    setIsOpen(false)
    setIsOpenConfirmationModal(false)
    setInputSearch('')
    setUser({} as UserBlockedFollowers)
  }

  useEffect(() => {
    modalUnbanMemberCommunityEventListener((event) => {
      const payload = (event as CustomEvent<UnbanMemberCommunityModalEventsPayload>).detail

      switch (payload.action) {
        case 'close':
          reset()
          break
        case 'open':
          payload?.community && setCommunity(payload.community)
          setIsOpen(true)
          break
        case 'openConfirmation':
          payload?.user && setUser(payload.user)
          payload?.fromConfigurationSidebar &&
            setFromConfigurationSidebar(payload.fromConfigurationSidebar)

          setIsOpenConfirmationModal(true)
          break
        case 'closeConfirmation':
          setIsOpenConfirmationModal(false)
          break
      }
    })
  }, [])

  const openUnbanMemberCommunityModal = useCallback((community: CommunityProps) => {
    modalUnbanMemberCommunityEventDispatch({ action: 'open', community })
  }, [])

  const closeUnbanMemberCommunityModal = useCallback(() => {
    modalUnbanMemberCommunityEventDispatch({ action: 'close' })
    reset()
  }, [])

  const openConfirmationUnbamModal = useCallback(() => {
    modalUnbanMemberCommunityEventDispatch({ action: 'openConfirmation' })
  }, [])

  const closeConfirmationUnbanMemberModal = useCallback(() => {
    modalUnbanMemberCommunityEventDispatch({ action: 'closeConfirmation' })
    reset()

    if (fromConfigurationSidebar) {
      setFromConfigurationSidebar(false)

      return null
    }

    return modalUnbanMemberCommunityEventDispatch({ action: 'open' })
  }, [fromConfigurationSidebar])

  const handleSetUser = useCallback((user) => {
    setUser(user)
  }, [])

  const handleSetCommunity = useCallback((community) => {
    setCommunity(community)
  }, [])

  return (
    <UnbanMemberCommunityModalContext.Provider
      value={{
        isOpen,
        openUnbanMemberCommunityModal,
        closeUnbanMemberCommunityModal,
        isOpenConfirmationModal,
        openConfirmationUnbamModal,
        closeConfirmationUnbanMemberModal,
        setInputSearch,
        community,
        handleSetCommunity,
        handleSetUser,
        inputSearch,
        fromConfigurationSidebar
      }}>
      {community?.id ? (
        <>
          <UnbanMemberCommunityModal isOpen={isOpen} closeModal={closeUnbanMemberCommunityModal} />

          <ConfirmationUnBanModal
            isOpen={isOpenConfirmationModal}
            community={community}
            user={user}
            closeModal={closeConfirmationUnbanMemberModal}
          />
        </>
      ) : null}

      {children}
    </UnbanMemberCommunityModalContext.Provider>
  )
}

export const useUnbanMemberCommunityModal = () => {
  const context = useContext(UnbanMemberCommunityModalContext)

  return context
}
